import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { Button } from '@/design-system/button';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { Commission } from '@/services/commissions/commissions.types';
import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ToggleStatusPayload } from '@/services/commissions/commissions.mutation';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { ManageCommissionCategoryForm } from '../ManageCommissionCategoryForm';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  onOpen: OnOpenProps,
  mutate: (payload: ToggleStatusPayload) => Promise<unknown>,
  t: (key: string) => string,
  openConfirmation: OpenConfirmationProps,
): ColumnDef<Commission>[] {
  return [
    {
      accessorKey: 'categoryId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="truncate max-w-[10rem] font-medium">{getSerialNumber(table, row.index)}</div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'categoryName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.categoryName')} />
      ),
      cell: ({ row }) => {
        const value = row.getValue<string>('categoryName');
        const className = value === 'Basic' ? 'text-success' : 'text-link';
        return row.original.status === 'A' ? (
          <Can
            rule={{ allOf: [PERMS.commission.type.defaultView, PERMS.commission.detail.list] }}
            fallback={<div className={`truncate max-w-[20rem] font-medium ${className}`}>{value}</div>}
          >
            <Link
              params={{
                categoryId: row.original.categoryId,
              }}
              to="/configuration-management/commissions/$categoryId"
              search={(previous) => ({
                categoryName: row.original.categoryName,
                createdDate: previous.createdDate,
                breadcrumbReturnTo: window.location.href,
              })}
            >
              <div className={`truncate max-w-[20rem] font-medium ${className} underline`}>{value}</div>
            </Link>
          </Can>
        ) : (
          <div className={`truncate max-w-[20rem] font-medium ${className}`}>{value}</div>
        );
      },
      meta: { label: 'Category Name' },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => <div className="text-primary line-clamp-2 font-medium">{row.getValue('description')}</div>,
      meta: { label: 'Description' },
      enableHiding: false,
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdBy')} />,
      cell: ({ row }) => <div className="font-medium">{row.getValue('createdBy')}</div>,
      meta: { label: 'Created By' },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue<Date>('createdDate'));

        return <div className="font-medium">{date ? date.toLocaleDateString() : '-'}</div>;
      },
      meta: { label: 'Created Date' },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => (
        <div className="font-medium">
          {row.getValue('status') === 'A' ? (
            <div className="text-success">{t('common.page.table.active')}</div>
          ) : (
            <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
          )}
        </div>
      ),
      meta: { label: 'Status' },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          {row.original.status === 'A' && (
            <Can
              rule={{
                allOf: [PERMS.commission.category.manage, PERMS.commission.category.detailView],
              }}
            >
              <EditIcon
                className="text-foreground-subtle size-4 hover:cursor-pointer"
                onClick={() =>
                  onOpen({
                    title: 'Manage Commission',
                    okText: <CrumbTitle id="common.page.button.submit" />,
                    size: 'lg',
                    modalTitle: 'configurationManagement.commissions.manageCommissionModal.title',
                    component: <ManageCommissionCategoryForm categoryId={row.getValue('categoryId')} />,
                  })
                }
              />
            </Can>
          )}

          <Can rule={{ allOf: [PERMS.commission.category.statusUpdate] }}>
            <Button
              variant={'ghost'}
              className="p-0"
              onClick={() => {
                const isActive = row.getValue('status') === 'A';
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: isActive
                    ? t('configurationManagement.commissions.page.deleteModel.blockCommissionCategory')
                    : t('configurationManagement.commissions.page.deleteModel.unblockCommissionCategory'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  onAction: async () => {
                    await mutate({
                      categoryId: row.getValue('categoryId'),
                      status: row.getValue('status') === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4 " />
              ) : (
                <LockOpenIcon className="text-link size-4 " />
              )}
            </Button>
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
